import { ChipAutocomplete } from '@/dsm-components/chip';
import { useLazySearchOrganizationsQuery } from '@/services/organizations';
import { useCallback, useMemo, useRef } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import { DialogFormValues } from './checklistItemInputOptions';
import { serializeParamsRaw } from './paramsRaw';

const debounceMs = 300;

interface OrganizationParamAdderProps {
  fieldPrefix: `conditions` | `inputs.${number}.conditions`;
  currentParams: string[];
  conditionIndex: number;
  setValue: UseFormSetValue<DialogFormValues>;
}

export const OrganizationParamAdder = ({
  fieldPrefix,
  currentParams,
  conditionIndex,
  setValue,
}: OrganizationParamAdderProps) => {
  const [trigger, { data, isFetching }] = useLazySearchOrganizationsQuery();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const items = useMemo(() => {
    if (!data?.flat) return [];
    return data.flat
      .filter((org) => !currentParams.includes(org.alias))
      .map((org) => ({ value: org.alias, label: org.name }));
  }, [data, currentParams]);

  const handleSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        trigger({ searchTerm: term });
      }, debounceMs);
    },
    [trigger],
  );

  const handleSelect = useCallback(
    (value: string) => {
      if (!currentParams.includes(value)) {
        const newParams = [...currentParams, value];
        setValue(`${fieldPrefix}.${conditionIndex}.paramsRaw`, serializeParamsRaw(newParams));
      }
    },
    [fieldPrefix, currentParams, conditionIndex, setValue],
  );

  return (
    <ChipAutocomplete
      items={items}
      onSelect={handleSelect}
      onSearch={handleSearch}
      isLoading={isFetching}
      placeholder="+ organization"
    />
  );
};
